import { Flex, Input, StyleProps, Text, useDisclosure } from "@chakra-ui/react";
import React from "react";

import {
  Button,
  ConfirmModal,
  successToast,
  useToast,
} from "../../../../components";
import { copy } from "../../../../utils/clipboard";
import { formatDate } from "../../../../utils/datetime";
import {
  useCreateGreenhouseAssessmentApiTokenMutationMutation,
  useGreenhouseAssessmentApiTokenQuery,
} from "../../../graphql";

type AssessmentApiKeySettingsProps = StyleProps & {
  //
};

export const AssessmentApiKeySettings: React.FC<
  AssessmentApiKeySettingsProps
> = ({ ...styles }) => {
  const toast = useToast();

  const { data, loading } = useGreenhouseAssessmentApiTokenQuery();
  const [createToken, { data: createTokenData, loading: createTokenLoading }] =
    useCreateGreenhouseAssessmentApiTokenMutationMutation({
      onCompleted: (data) => {
        if (data.createGreenhouseAssessmentApiToken?.token) {
          successToast(
            toast,
            "API key created. Copy the key and share it with your Greenhouse account manager.",
            { duration: 10_000, isClosable: true }
          );
        }
      },
    });

  const confirmModal = useDisclosure();

  const tokenPrefix = data?.assessmentApiToken?.tokenPrefix
    ? `${data.assessmentApiToken.tokenPrefix}......`
    : "";
  const tokenCreatedAt = data?.assessmentApiToken?.createdAt;
  const hasToken = !!tokenPrefix;

  const newToken =
    createTokenData?.createGreenhouseAssessmentApiToken?.token ?? "";

  if (loading) {
    return null;
  }

  const copyToken = (): void => {
    copy(newToken);
    toast.closeAll();
    successToast(toast, "API key copied to clipboard");
  };

  return (
    <Flex direction="column" gap={2} {...styles}>
      <Text fontWeight="semibold">AI Interviewer API key</Text>
      <Text>
        {newToken
          ? "Copy this API key and share it with your Greenhouse account manager. You will not be able to access this key again after you leave this page."
          : tokenCreatedAt
          ? `This API key was created on ${formatDate(tokenCreatedAt)}.`
          : "Create an API key and share it with your Greenhouse account manager to enable the AI Interviewer."}
      </Text>
      <Flex gap="2">
        <Input
          isReadOnly
          placeholder="No API key generated"
          value={newToken || tokenPrefix}
          onClick={(e) => {
            if (newToken) {
              e.currentTarget.select();
            }
          }}
          flex="1"
        />
        {newToken ? (
          <Button onClick={copyToken}>Copy</Button>
        ) : (
          <Button
            onClick={hasToken ? confirmModal.onOpen : () => createToken()}
            isLoading={createTokenLoading}
          >
            {hasToken ? "Regenerate" : "Generate"}
          </Button>
        )}
      </Flex>
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onCancel={confirmModal.onClose}
        onConfirm={() => {
          confirmModal.onClose();
          createToken();
        }}
        modalTitleText="Regenerate API key"
        modalBodyText="Are you sure you want to generate a new API key? This will invalidate your existing API key."
        modalSubmitText="Regenerate"
      />
    </Flex>
  );
};
